import { useEffect, useState } from "react";

import { listCostCenterGroupsApi, listCostCentersApi } from "../api/financeiroApi";
import type { FinCostCenter, FinCostCenterGroup } from "../api/finTypes";

/** Opções do `<select>` de centro de custo, em `<optgroup>` por grupo e subgrupo. */
export type CostCenterOptionGroup = { key: string; label: string; options: FinCostCenter[] };

const byCode = (a: FinCostCenter, b: FinCostCenter) => a.code.localeCompare(b.code, "pt-BR", { numeric: true });

/**
 * Agrupa os centros pelo grupo ("Operacional") ou subgrupo ("Operacional › Rio"),
 * na ordem da árvore de grupos; os sem grupo (ou de grupo inativo) vão por último,
 * em "Sem grupo".
 */
export function groupCostCenterOptions(costCenters: FinCostCenter[], groups: FinCostCenterGroup[]): CostCenterOptionGroup[] {
  const out: CostCenterOptionGroup[] = [];
  const placed = new Set<string>();
  const push = (key: string, label: string, groupId: string) => {
    const options = costCenters.filter((c) => c.group?.id === groupId).sort(byCode);
    options.forEach((o) => placed.add(o.id));
    if (options.length) out.push({ key, label, options });
  };
  for (const root of groups) {
    push(root.id, `${root.code} — ${root.name}`, root.id);
    for (const sub of root.children) push(sub.id, `${sub.code} — ${root.name} › ${sub.name}`, sub.id);
  }
  const rest = costCenters.filter((c) => !placed.has(c.id)).sort(byCode);
  if (rest.length) out.push({ key: "none", label: groups.length ? "Sem grupo" : "Centros de custo", options: rest });
  return out;
}

/** "2.1 Operacional › Rio › 2 — Rio de Janeiro" a partir do centro como o título o devolve. */
export function costCenterPath(cc: {
  code: string;
  name: string;
  group?: { code?: string; name: string; parent: { name: string } | null } | null;
} | null): string {
  if (!cc) return "—";
  const group = cc.group ? `${cc.group.code ? `${cc.group.code} ` : ""}${[cc.group.parent?.name, cc.group.name].filter(Boolean).join(" › ")}` : null;
  return [group, `${cc.code} — ${cc.name}`].filter(Boolean).join(" › ");
}

/** Centros de custo ativos e a árvore de grupos, carregados juntos. */
export function useCostCenters(token: string) {
  const [costCenters, setCostCenters] = useState<FinCostCenter[]>([]);
  const [groups, setGroups] = useState<FinCostCenterGroup[]>([]);
  useEffect(() => {
    if (!token) return;
    listCostCentersApi(token).then(setCostCenters).catch(() => setCostCenters([]));
    listCostCenterGroupsApi(token).then(setGroups).catch(() => setGroups([]));
  }, [token]);
  return { costCenters, groups };
}

/** Ids dos centros de um grupo e dos subgrupos dele (para filtros locais). */
export function costCenterIdsOfGroup(groups: FinCostCenterGroup[], groupId: string): string[] {
  const walk = (nodes: FinCostCenterGroup[]): string[] =>
    nodes.flatMap((g) => (g.id === groupId ? collect(g) : walk(g.children)));
  const collect = (g: FinCostCenterGroup): string[] => [...g.costCenters.map((c) => c.id), ...g.children.flatMap(collect)];
  return walk(groups);
}
